import jsPDF from 'jspdf';
import { TestResults } from '../../../../types';
import { PDF_COLORS } from '../../config/colors';
import { PDF_FONTS, PDF_LAYOUT } from '../../config/fonts';
import { PageManager } from '../../helpers/pageBreaks';

export class ExecutiveSummaryComponent {
  private pdf: jsPDF;
  private pageManager: PageManager;

  constructor(pdf: jsPDF, pageManager: PageManager) {
    this.pdf = pdf;
    this.pageManager = pageManager;
  }

  render(results: TestResults): void {
    const summaryHeight = 70;
    this.pageManager.checkForPageBreak(summaryHeight, {
      minSpaceAfter: 30,
      elementType: 'executive-summary',
    });

    // Summary container
    this.pdf.setFillColor(...PDF_COLORS.background.primary);
    this.pdf.setDrawColor(...PDF_COLORS.primary);
    this.pdf.rect(
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY(),
      this.pageManager.getContentWidth(),
      50,
      'FD'
    );

    let currentY = this.pageManager.getCurrentY() + 12;

    // Summary title
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.subheading);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text('Executive Summary', PDF_LAYOUT.margins.left + 5, currentY);

    currentY += 15;

    // Summary content
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.primary);

    const summaryText = this.generateSummaryText(results);
    const summaryLines = this.pdf.splitTextToSize(
      summaryText,
      this.pageManager.getContentWidth() - 10
    );

    summaryLines.forEach((line: string, index: number) => {
      if (currentY > this.pageManager.getCurrentY() + 45) return;
      this.pdf.text(line, PDF_LAYOUT.margins.left + 5, currentY);
      currentY += 6;
    });

    this.pageManager.addElement(60, {
      spacingAfter: PDF_LAYOUT.spacing.section,
      elementType: 'executive-summary',
    });
  }

  private generateSummaryText(results: TestResults): string {
    const readyCount = results.categories.filter(c => c.status === 'Ready').length;
    const almostReadyCount = results.categories.filter(c => c.status === 'Almost Ready').length;
    const notReadyCount = results.categories.filter(c => c.status === 'Not Ready').length;

    if (results.overallPercentage >= 80) {
      return `Congratulations! With an overall score of ${Math.round(results.overallPercentage)}%, your business demonstrates strong export readiness. You have ${readyCount} categories at "Ready" status. Focus on fine-tuning the remaining areas to achieve comprehensive export preparedness.`;
    } else if (results.overallPercentage >= 60) {
      return `Your business shows good export potential with a score of ${Math.round(results.overallPercentage)}%. You have ${readyCount + almostReadyCount} categories that are ready or almost ready. With focused improvements in ${notReadyCount} key areas, you can achieve full export readiness within 6-12 months.`;
    } else {
      return `Your assessment score of ${Math.round(results.overallPercentage)}% indicates significant preparation is needed before pursuing export markets. Focus on building foundational capabilities in the ${notReadyCount} categories requiring attention. This is a normal starting point for many businesses new to exporting.`;
    }
  }
}